import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '@app/root/providers/toast-service';
import { AuthService } from '@services/auth.service';
import { AuthStorage } from '@services/auth.storage';
import { fromEvent, interval, merge } from 'rxjs';

/**
 * Signs the user out after a period without any user interaction (HIPAA automatic logoff).
 * Activity is kept in localStorage so that activity in any tab keeps all tabs of the app alive.
 * The auth service enforces its own idle limit on refresh; this one is the precise, user-facing one.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private toastService = inject(ToastService);

  static readonly IDLE_TIMEOUT_MS = 30 * 60 * 1000;
  static readonly WARNING_BEFORE_MS = 60 * 1000;
  static readonly CHECK_INTERVAL_MS = 15 * 1000;
  static readonly ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
  static readonly ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'];
  // Patient-facing flows that manage their own session end
  static readonly EXCLUDED_ROUTES = ['/terminal', '/actions'];

  private started = false;
  private warned = false;
  private lastActivityWrite = 0;

  start() {
    if (this.started) return;
    this.started = true;
    this.ngZone.runOutsideAngular(() => {
      const events = IdleTimeoutService.ACTIVITY_EVENTS.map(name => fromEvent(document, name, { passive: true, capture: true }));
      merge(...events).subscribe(() => this.recordActivity());
      interval(IdleTimeoutService.CHECK_INTERVAL_MS).subscribe(() => this.check());
    });
  }

  recordActivity(now = Date.now()) {
    // After sleep or a throttled tab the interval may not have run yet - the deadline must win over new activity
    if (this.isTracked() && this.isIdleExpired(now)) {
      this.check(now);
      return;
    }
    this.warned = false;
    if (now - this.lastActivityWrite < IdleTimeoutService.ACTIVITY_WRITE_THROTTLE_MS) return;
    this.lastActivityWrite = now;
    localStorage.setItem(AuthStorage.LAST_ACTIVITY, now.toString());
  }

  check(now = Date.now()) {
    if (!this.isTracked()) {
      this.warned = false;
      return;
    }

    const idleFor = now - this.getLastActivity(now);
    if (idleFor >= IdleTimeoutService.IDLE_TIMEOUT_MS) {
      this.ngZone.run(() => {
        this.authService.logout('IdleTimeout');
        this.toastService.showWarning('You have been signed out due to inactivity.');
      });
    } else if (idleFor >= IdleTimeoutService.IDLE_TIMEOUT_MS - IdleTimeoutService.WARNING_BEFORE_MS && !this.warned) {
      this.warned = true;
      this.ngZone.run(() => this.toastService.showWarning('You will be signed out in 1 minute due to inactivity.'));
    }
  }

  private isTracked() {
    return this.authService.authStatus() && !this.isExcludedRoute();
  }

  private isIdleExpired(now: number) {
    const lastActivity = Number(localStorage.getItem(AuthStorage.LAST_ACTIVITY));
    return lastActivity > 0 && now - lastActivity >= IdleTimeoutService.IDLE_TIMEOUT_MS;
  }

  private isExcludedRoute() {
    const path = this.router.url.split(/[?#]/)[0];
    return IdleTimeoutService.EXCLUDED_ROUTES.some(route => path === route || path.startsWith(`${route}/`));
  }

  private getLastActivity(now: number) {
    const lastActivity = Number(localStorage.getItem(AuthStorage.LAST_ACTIVITY));
    if (lastActivity > 0) return lastActivity;
    // No activity recorded yet in this session (fresh login clears storage) - start counting now
    localStorage.setItem(AuthStorage.LAST_ACTIVITY, now.toString());
    return now;
  }
}
